// DISCLAIMER: Este documento é uma base inicial para fins informativos e NÃO substitui
// assessoria jurídica especializada. Recomenda-se revisão por advogado com expertise em
// direito digital e relações de consumo antes de publicar em produção.

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MarketingLayout } from '../marketing/marketing-layout';

export function TermsPage() {
  const { t } = useTranslation();

  return (
    <MarketingLayout
      title={t('terms.pageTitle', 'Termos de Uso')}
      subtitle={t('terms.pageSubtitle', 'Condições que regem o uso da plataforma reformu.lat.')}
    >
      <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8 text-muted-foreground">

        {/* Last updated */}
        <p className="text-sm border border-border rounded-lg px-4 py-3 bg-muted/30 text-muted-foreground">
          <strong className="text-foreground">{t('terms.lastUpdatedLabel', 'Última atualização:')}</strong>{' '}
          {t('terms.lastUpdatedDate', '29 de junho de 2026')}
        </p>

        <p>
          {t('terms.introBeforeBrand', 'Ao criar uma conta ou utilizar a ')}
          <strong className="text-foreground">reformu.lat</strong>
          {t(
            'terms.introAfterBrand',
            ', você concorda com estes Termos de Uso. Leia-os com atenção. Se não concordar com alguma cláusula, não utilize o serviço.'
          )}
        </p>

        {/* 1 — Descrição do Serviço */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('terms.section1Title', '1. Descrição do Serviço')}
          </h2>
          <p>
            {t('terms.section1IntroBeforeBrand', 'A ')}
            <strong className="text-foreground">reformu.lat</strong>
            {t(
              'terms.section1IntroAfterBrand',
              ' é uma plataforma SaaS (Software as a Service) brasileira que oferece ferramentas para escrita e publicação de livros com assistência de inteligência artificial. Os recursos incluem, entre outros:'
            )}
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-3">
            <li>{t('terms.feature1', 'Editor de texto colaborativo com ghostwriter de IA inline')}</li>
            <li>{t('terms.feature2', 'Story Bible para gerenciamento de personagens, locais e lore')}</li>
            <li>{t('terms.feature3', 'Outline Board para planejamento visual da estrutura narrativa')}</li>
            <li>{t('terms.feature4', 'Exportação em PDF, EPUB e DOCX com formatação profissional')}</li>
            <li>{t('terms.feature5', 'Geração de capa por IA')}</li>
            <li>{t('terms.feature6', 'Transcrição de áudio via Whisper (OpenAI)')}</li>
            <li>{t('terms.feature7', 'Aplicativo nativo para Android')}</li>
          </ul>
          <p className="mt-3">
            {t(
              'terms.section1Note',
              'A reformu.lat se reserva o direito de adicionar, modificar ou descontinuar funcionalidades, com aviso prévio razoável nos casos de alterações significativas.'
            )}
          </p>
        </section>

        {/* 2 — Conta e Responsabilidades */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('terms.section2Title', '2. Conta de Usuário e Responsabilidades')}
          </h2>
          <p>
            {t(
              'terms.section2Intro',
              'Para acessar a maioria das funcionalidades, você precisa criar uma conta com nome e e-mail válidos. Você é responsável por:'
            )}
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-3">
            <li>{t('terms.responsibility1', 'Manter a confidencialidade das suas credenciais de acesso.')}</li>
            <li>{t('terms.responsibility2', 'Todas as atividades realizadas na conta, autorizadas ou não.')}</li>
            <li>{t('terms.responsibility3', 'Notificar imediatamente a reformu.lat em caso de acesso não autorizado.')}</li>
            <li>{t('terms.responsibility4', 'Fornecer informações verdadeiras no momento do cadastro e mantê-las atualizadas.')}</li>
          </ul>
          <p className="mt-3">
            {t(
              'terms.section2MinorsNote',
              'Contas criadas por menores de 18 anos requerem autorização e supervisão de um responsável legal.'
            )}
          </p>
        </section>

        {/* 3 — Propriedade do Conteúdo */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('terms.section3Title', '3. Propriedade do Conteúdo — Você é Dono do que Escreve')}
          </h2>
          <p>
            {t(
              'terms.section3Ownership',
              'Todo o conteúdo original que você cria, escreve ou importa na plataforma — manuscritos, rascunhos, notas, Story Bible, outline — é e permanece de sua propriedade. A reformu.lat não reivindica qualquer direito autoral sobre o conteúdo criado por você.'
            )}
          </p>
          <p className="mt-3">
            {t(
              'terms.section3LicenseIntro',
              'Ao utilizar a plataforma, você concede à reformu.lat uma licença limitada, não exclusiva, irrevogável e mundial, exclusivamente para:'
            )}
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-3">
            <li>{t('terms.license1', 'Armazenar, processar e transmitir seu conteúdo para prestação do serviço.')}</li>
            <li>{t('terms.license2', 'Enviar extratos de texto à API de IA (OpenAI) para gerar sugestões no editor.')}</li>
            <li>{t('terms.license3', 'Realizar backups e garantir a continuidade do serviço.')}</li>
          </ul>
          <p className="mt-3">
            {t(
              'terms.section3LicenseRevocationBeforeStrong',
              'Esta licença é revogada automaticamente quando você exclui o conteúdo ou encerra sua conta. A reformu.lat '
            )}
            <strong className="text-foreground">
              {t('terms.section3LicenseRevocationStrong', 'não usa')}
            </strong>
            {t(
              'terms.section3LicenseRevocationAfterStrong',
              ' o seu conteúdo para treinar modelos de IA proprietários.'
            )}
          </p>
        </section>

        {/* 4 — Uso Aceitável */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('terms.section4Title', '4. Uso Aceitável')}
          </h2>
          <p>
            {t(
              'terms.section4Intro',
              'Você se compromete a usar a plataforma somente para fins lícitos e de acordo com estes Termos. É expressamente proibido:'
            )}
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-3">
            <li>{t('terms.prohibited1', 'Criar, armazenar ou publicar conteúdo ilegal, discriminatório, que incite violência ou que viole direitos de terceiros.')}</li>
            <li>{t('terms.prohibited2', 'Produzir conteúdo de abuso sexual de menores (CSAM) ou material que explore vulnerabilidade infantil.')}</li>
            <li>{t('terms.prohibited3', 'Usar a plataforma para gerar desinformação, spam ou conteúdo fraudulento.')}</li>
            <li>{t('terms.prohibited4', 'Realizar engenharia reversa, decompilar ou tentar extrair o código-fonte da plataforma.')}</li>
            <li>{t('terms.prohibited5', 'Contornar, desabilitar ou interferir em mecanismos de segurança, autenticação ou controle de acesso.')}</li>
            <li>{t('terms.prohibited6', 'Acessar contas de outros usuários sem autorização expressa.')}</li>
            <li>{t('terms.prohibited7', 'Usar scripts automatizados, bots ou ferramentas para uso indevido da API além dos limites acordados.')}</li>
            <li>{t('terms.prohibited8', 'Revender, sublicenciar ou explorar comercialmente o acesso ao serviço sem autorização prévia por escrito.')}</li>
          </ul>
          <p className="mt-3">
            {t(
              'terms.section4Note',
              'A violação desta seção pode resultar em suspensão ou encerramento imediato da conta, sem direito a reembolso.'
            )}
          </p>
        </section>

        {/* 5 — Planos, Cobrança e Cancelamento */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('terms.section5Title', '5. Planos, Cobrança, Trial e Cancelamento')}
          </h2>

          <h3 className="text-foreground text-base font-semibold mt-4 mb-2">
            {t('terms.section51Title', '5.1 Plano Gratuito / Trial')}
          </h3>
          <p>
            {t(
              'terms.section51Text',
              'A reformu.lat oferece um plano gratuito ou período de trial com funcionalidades limitadas. O trial pode ser encerrado ou modificado a qualquer momento pela reformu.lat, com aviso prévio quando aplicável.'
            )}
          </p>

          <h3 className="text-foreground text-base font-semibold mt-4 mb-2">
            {t('terms.section52Title', '5.2 Planos Pagos')}
          </h3>
          <p>
            {t(
              'terms.section52TextBeforeStrong',
              'Os planos pagos são cobrados de forma recorrente (mensal ou anual) conforme o plano escolhido no momento da assinatura. Os pagamentos são processados pelo nosso parceiro '
            )}
            <strong className="text-foreground">Asaas Gestão Financeira S.A.</strong>
            {t('terms.section52TextAfterStrong', ', autorizado pelo Banco Central do Brasil.')}
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-3">
            <li>
              {t('terms.section52Item1Before', 'Os valores dos planos estão disponíveis na página de')}{' '}
              <Link to="/pricing" className="text-primary underline underline-offset-4">
                {t('terms.pricesLinkText', 'Preços')}
              </Link>
              {t('terms.section52Item1After', '.')}
            </li>
            <li>{t('terms.section52Item2', 'A cobrança é realizada no início de cada ciclo de faturamento.')}</li>
            <li>{t('terms.section52Item3', 'Preços podem ser reajustados com aviso de pelo menos 30 dias de antecedência.')}</li>
          </ul>

          <h3 className="text-foreground text-base font-semibold mt-4 mb-2">
            {t('terms.section53Title', '5.3 Cancelamento')}
          </h3>
          <p>
            {t(
              'terms.section53Text',
              'Você pode cancelar sua assinatura a qualquer momento, diretamente nas configurações da conta ou entrando em contato com o suporte. O cancelamento tem efeito no final do ciclo de faturamento vigente, sem cobranças adicionais. Não há reembolso proporcional de períodos já pagos, exceto nos casos previstos no Código de Defesa do Consumidor (CDC) brasileiro.'
            )}
          </p>

          <h3 className="text-foreground text-base font-semibold mt-4 mb-2">
            {t('terms.section54Title', '5.4 Direito de Arrependimento (CDC)')}
          </h3>
          <p>
            {t(
              'terms.section54TextBeforeStrong',
              'Para compras realizadas à distância, você tem o direito de arrependimento e cancelamento em até '
            )}
            <strong className="text-foreground">{t('terms.section54Strong', '7 dias corridos')}</strong>{' '}
            {t(
              'terms.section54TextAfterStrong',
              'após a contratação, com direito a reembolso integral, conforme o Art. 49 do Código de Defesa do Consumidor (Lei 8.078/1990).'
            )}
          </p>
        </section>

        {/* 6 — IA e Isenções */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('terms.section6Title', '6. Conteúdo Gerado por Inteligência Artificial — Isenções e Responsabilidade')}
          </h2>
          <p>
            {t(
              'terms.section6Intro',
              'As funcionalidades de IA da reformu.lat geram texto, sugestões e transcrições de forma automatizada. Você deve estar ciente de que:'
            )}
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-3">
            <li>
              <strong className="text-foreground">
                {t('terms.aiItem1Strong', 'O conteúdo gerado por IA pode conter imprecisões,')}
              </strong>{' '}
              {t(
                'terms.aiItem1Rest',
                'informações desatualizadas, alucinações factuais ou inconsistências narrativas. A revisão e validação de todo o conteúdo gerado são de responsabilidade exclusiva do usuário.'
              )}
            </li>
            <li>
              {t(
                'terms.aiItem2',
                'A reformu.lat não garante que sugestões de IA sejam originais, livres de direitos de terceiros ou adequadas para publicação sem revisão humana.'
              )}
            </li>
            <li>
              {t(
                'terms.aiItem3',
                'O usuário é o único responsável pelo conteúdo final publicado ou distribuído, independentemente de ter sido parcialmente gerado por IA.'
              )}
            </li>
            <li>
              {t(
                'terms.aiItem4',
                'A qualidade e disponibilidade das funcionalidades de IA dependem dos serviços de terceiros (OpenAI), podendo variar ou ser interrompidas por fatores fora do controle da reformu.lat.'
              )}
            </li>
          </ul>
        </section>

        {/* 7 — Limitação de Responsabilidade */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('terms.section7Title', '7. Limitação de Responsabilidade')}
          </h2>
          <p>
            {t('terms.section7Intro', 'Na máxima extensão permitida pela legislação aplicável:')}
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-3">
            <li>
              {t(
                'terms.liability1',
                'A reformu.lat é fornecida "como está" e "conforme disponível", sem garantias expressas ou implícitas de disponibilidade ininterrupta, adequação a fins específicos ou ausência de erros.'
              )}
            </li>
            <li>
              {t(
                'terms.liability2',
                'A reformu.lat não se responsabiliza por perdas de dados causadas por falha do usuário em manter backups locais, por eventos de força maior ou por falhas de serviços de terceiros.'
              )}
            </li>
            <li>
              {t(
                'terms.liability3',
                'Em nenhum caso a responsabilidade total da reformu.lat por danos diretos excederá o valor pago pelo usuário nos 3 meses anteriores ao evento que originou o dano.'
              )}
            </li>
            <li>
              {t(
                'terms.liability4',
                'A reformu.lat não é responsável por danos indiretos, lucros cessantes, danos morais decorrentes de perda de conteúdo ou interrupção do serviço, salvo nas hipóteses previstas no CDC.'
              )}
            </li>
          </ul>
          <p className="mt-3">
            {t(
              'terms.section7Note',
              'As limitações acima não se aplicam a casos de dolo ou culpa grave da reformu.lat, nem prejudicam direitos do consumidor previstos em lei.'
            )}
          </p>
        </section>

        {/* 8 — Rescisão */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('terms.section8Title', '8. Rescisão')}
          </h2>
          <p>
            {t(
              'terms.section8Intro',
              'A reformu.lat pode suspender ou encerrar sua conta, com ou sem aviso prévio, nas seguintes situações:'
            )}
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-3">
            <li>{t('terms.termination1', 'Violação destes Termos de Uso.')}</li>
            <li>{t('terms.termination2', 'Uso fraudulento, abusivo ou que coloque outros usuários ou a plataforma em risco.')}</li>
            <li>{t('terms.termination3', 'Inadimplência no pagamento das assinaturas após período de carência.')}</li>
            <li>{t('terms.termination4', 'Descontinuação do serviço (com aviso mínimo de 30 dias).')}</li>
          </ul>
          <p className="mt-3">
            {t('terms.section8DeleteBefore', 'Você pode solicitar a exclusão da conta a qualquer momento pelo e-mail')}{' '}
            <a href="mailto:diegosierravibes@gmail.com" className="text-primary underline underline-offset-4">
              diegosierravibes@gmail.com
            </a>
            {t('terms.section8DeleteMiddle', '. Após a exclusão, seus dados serão removidos conforme descrito em nossa')}{' '}
            <Link to="/privacidade" className="text-primary underline underline-offset-4">
              {t('terms.privacyPolicyLinkText', 'Política de Privacidade')}
            </Link>
            {t('terms.section8DeleteEnd', '.')}
          </p>
        </section>

        {/* 9 — Legislação e Foro */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('terms.section9Title', '9. Legislação Aplicável e Foro')}
          </h2>
          <p>
            {t(
              'terms.section9Text1',
              'Estes Termos de Uso são regidos e interpretados de acordo com as leis da República Federativa do Brasil, em especial o Código de Defesa do Consumidor (Lei 8.078/1990), o Marco Civil da Internet (Lei 12.965/2014) e a Lei Geral de Proteção de Dados (Lei 13.709/2018).'
            )}
          </p>
          <p className="mt-3">
            {t(
              'terms.section9Text2',
              'Para resolução de disputas, fica eleito o foro da comarca da sede da reformu.lat no Brasil, com expressa renúncia a qualquer outro, por mais privilegiado que seja, salvo nos casos em que a lei assegure ao consumidor o direito de demandar no foro de seu domicílio.'
            )}
          </p>
        </section>

        {/* 10 — Contato */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('terms.section10Title', '10. Contato')}
          </h2>
          <p>
            {t('terms.section10Intro', 'Dúvidas, sugestões ou questões sobre estes Termos de Uso? Entre em contato:')}
          </p>
          <div className="mt-4 rounded-xl border border-border p-5 space-y-1">
            <p className="text-sm">
              <strong className="text-foreground">{t('terms.emailLabel', 'E-mail:')}</strong>{' '}
              <a href="mailto:diegosierravibes@gmail.com" className="text-primary underline underline-offset-4">
                diegosierravibes@gmail.com
              </a>
            </p>
            <p className="text-sm text-muted-foreground/70">
              {t('terms.responseTime', 'Tempo de resposta: até 5 dias úteis.')}
            </p>
          </div>
          <p className="mt-4 text-sm">
            {t('terms.section10FooterBefore', 'Veja também nossa')}{' '}
            <Link to="/privacidade" className="text-primary underline underline-offset-4">
              {t('terms.privacyPolicyLinkText', 'Política de Privacidade')}
            </Link>{' '}
            {t('terms.section10FooterAfter', 'para entender como tratamos seus dados pessoais.')}
          </p>
        </section>

      </div>
    </MarketingLayout>
  );
}
